/**
 * Wire types for the expert review desk. These mirror backend/app/schemas/item_review.py
 * one-for-one. Axis names are deliberately generic (`axisA` / `axisB`) because the
 * backend blueprint carries two NAMED axes: for INBDE they are Foundation Knowledge and
 * Clinical Content, for another exam they are something else entirely. The desk reads
 * the labels off the context payload rather than hardcoding an exam's vocabulary.
 */

export type Provenance = 'ai_unreviewed' | 'in_review' | 'verified' | 'rejected';
export type ReviewActionName = 'approve' | 'reject' | 'flag' | 'unflag' | 'reopen';

export interface ReviewerContext {
  syllabus_key: string;
  exam_label: string;
  role: 'sme' | 'lead';
  display_credential: string | null;
  counts: Record<string, number>;
  reviewed_by_me_today: number;
  reason_codes: string[];
  axis_a_label: string;
  axis_b_label: string;
}

export interface QueueItem {
  id: string;
  question_id: string;
  stem_preview: string;
  axis_a_label: string | null;
  axis_b_label: string | null;
  group_label: string | null;
  provenance: Provenance;
  flagged: boolean;
  is_case: boolean;
  review_count: number;
  created_at: string;
}

export interface OptionDetail {
  id: string;
  text: string;
  is_key: boolean;
  concept_id: string | null;
  concept_name: string | null;
  misconception: string | null;
  rationale_md: string | null;
}

export interface ReviewEvent {
  id: string;
  action: string;
  reason_code: string | null;
  notes: string | null;
  reviewer_name: string | null;
  created_at: string;
}

export interface StatementBlock {
  id?: string;
  type: 'text' | 'formula' | 'figure';
  md?: string;
  latex?: string;
  svg?: string | null;
}

export interface ItemDetail {
  id: string;
  question_id: string;
  syllabus_key: string;
  spec_version: string;
  statement_blocks: StatementBlock[];
  patient_box: Record<string, string> | null;
  options: OptionDetail[];
  key_option_id: string;
  axis_a_id: number;
  axis_a_label: string | null;
  axis_b_id: number;
  axis_b_label: string | null;
  group_label: string | null;
  task_type: string | null;
  provenance: Provenance;
  flagged: boolean;
  case_id: string | null;
  case_size: number;
  history: ReviewEvent[];
  answered_count: number;
}

export interface OptionEdit {
  id: string;
  text: string;
  is_key: boolean;
  concept_id?: string | null;
  misconception?: string | null;
  rationale_md?: string | null;
}

export interface ItemEditPayload {
  stem_md?: string;
  patient_box?: Record<string, string> | null;
  options?: OptionEdit[];
  axis_a_id?: number;
  axis_b_id?: number;
  task_type?: string | null;
  notes?: string | null;
  seconds_spent?: number;
}

export interface ReviewStats {
  total_reviewed: number;
  approved: number;
  rejected: number;
  edited: number;
  approval_rate: number | null;
  median_seconds: number | null;
  by_reason: { reason_code: string; count: number }[];
}

/** Human labels for the rejection taxonomy. The codes themselves are the contract. */
export const REASON_LABELS: Record<string, string> = {
  key_wrong: 'Keyed answer is wrong',
  multiple_defensible: 'More than one answer defensible',
  distractor_implausible: 'Distractor nobody would pick',
  cueing: 'Wording gives it away',
  out_of_scope: 'Outside entry-level practice',
  cell_mismatch: 'Does not test the tagged area',
  format: 'Breaks item-writing rules',
  duplicate: 'Duplicate of another item',
  outdated: 'Not current standard of care',
  ambiguous_stem: 'Stem is ambiguous',
  other: 'Something else',
};

export const PROVENANCE_LABELS: Record<Provenance, string> = {
  ai_unreviewed: 'Not yet reviewed',
  in_review: 'In review',
  verified: 'Verified',
  rejected: 'Rejected',
};

/** The four patient-box fields, in the order the real exam prints them. */
export const PATIENT_FIELDS: { key: string; label: string }[] = [
  { key: 'patient', label: 'Patient' },
  { key: 'chief_complaint', label: 'Chief complaint' },
  { key: 'background', label: 'Background' },
  { key: 'findings', label: 'Findings' },
];

export const MIN_REJECT_NOTE = 15;
